/**
 * Détail de surface des docks (porteur, baie de la station) : textures répétables plaquées en projection
 * triplanaire dans le repère du dock, à l'échelle du mètre. Une structure de plusieurs centaines de mètres reste
 * détaillée de près (tunnel), ce qu'une texture unique ne permet pas.
 * - hors du tunnel : panneaux de coque ; dans le volume du tunnel : plaques de hangar ;
 * - la couleur de la texture se mélange à celle du matériau, la carte de normales ajoute le relief des joints.
 * Textures générées avec Meshy (text-to-image), cartes dérivées par tools/assets/tile-maps.mjs.
 */
import {
  Matrix,
  MaterialPluginBase,
  ShaderLanguage,
  Texture,
  Vector3,
  type AbstractEngine,
  type BaseTexture,
  type Material,
  type MaterialDefines,
  type Scene,
  type SubMesh,
  type UniformBuffer,
} from '@babylonjs/core';
import { assetUrl } from '../assets';

export interface SurfaceDetailSet {
  hullAlbedo: Texture;
  hullNormal: Texture;
  hangarAlbedo: Texture;
  hangarNormal: Texture;
}

export function loadSurfaceDetail(scene: Scene): SurfaceDetailSet {
  const tex = (name: string, srgb: boolean) => {
    const t = new Texture(assetUrl(`textures/${name}`), scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);
    t.anisotropicFilteringLevel = 8;
    t.gammaSpace = srgb;
    return t;
  };
  return {
    hullAlbedo: tex('hull_panels_albedo.jpg', true),
    hullNormal: tex('hull_panels_normal.png', false),
    hangarAlbedo: tex('hangar_panels_albedo.jpg', true),
    hangarNormal: tex('hangar_panels_normal.png', false),
  };
}

export interface SurfaceDetailOptions {
  /** Taille d'un motif (m) : coque, hangar. */
  hullTile: number;
  hangarTile: number;
  /** Part de la couleur de la texture (0 = couleur du matériau seule). */
  albedo: number;
  /** Force du relief. */
  normal: number;
}

export class SurfaceDetailPlugin extends MaterialPluginBase {
  /** Monde → repère du dock. */
  readonly invWorld = Matrix.Identity();
  /** Demi-dimensions du volume « hangar » (tunnel élargi aux parois), repère du dock. */
  readonly hangar = new Vector3();

  constructor(material: Material, private readonly set: SurfaceDetailSet, private options: SurfaceDetailOptions) {
    super(material, 'SurfaceDetail', 210, { SURFACEDETAIL: false });
    this._enable(true);
  }

  configure(options: SurfaceDetailOptions): void {
    this.options = options;
  }

  override prepareDefines(defines: MaterialDefines): void {
    defines['SURFACEDETAIL'] = true;
  }

  override isReadyForSubMesh(): boolean {
    return Object.values(this.set).every((t: Texture) => t.isReady());
  }

  override getClassName(): string {
    return 'SurfaceDetailPlugin';
  }

  override isCompatible(shaderLanguage: ShaderLanguage): boolean {
    return shaderLanguage === ShaderLanguage.GLSL;
  }

  override getSamplers(samplers: string[]): void {
    samplers.push('sdHullAlbedo', 'sdHullNormal', 'sdHangarAlbedo', 'sdHangarNormal');
  }

  override getActiveTextures(activeTextures: BaseTexture[]): void {
    activeTextures.push(...Object.values(this.set));
  }

  override hasTexture(texture: BaseTexture): boolean {
    return Object.values(this.set).includes(texture as Texture);
  }

  override getUniforms() {
    return {
      ubo: [
        { name: 'sdInvWorld', size: 16, type: 'mat4' },
        { name: 'sdHangar', size: 3, type: 'vec3' },
        { name: 'sdParams', size: 4, type: 'vec4' },
      ],
      fragment: `#ifdef SURFACEDETAIL
uniform mat4 sdInvWorld;
uniform vec3 sdHangar;
uniform vec4 sdParams;
#endif`,
    };
  }

  override bindForSubMesh(uniformBuffer: UniformBuffer, _scene: Scene, _engine: AbstractEngine, subMesh: SubMesh): void {
    uniformBuffer.updateMatrix('sdInvWorld', this.invWorld);
    uniformBuffer.updateVector3('sdHangar', this.hangar);
    const o = this.options;
    uniformBuffer.updateFloat4('sdParams', o.hullTile, o.hangarTile, o.albedo, o.normal);
    const effect = subMesh.effect;
    if (!effect) return;
    effect.setTexture('sdHullAlbedo', this.set.hullAlbedo);
    effect.setTexture('sdHullNormal', this.set.hullNormal);
    effect.setTexture('sdHangarAlbedo', this.set.hangarAlbedo);
    effect.setTexture('sdHangarNormal', this.set.hangarNormal);
  }

  override getCustomCode(shaderType: string): Record<string, string> | null {
    if (shaderType !== 'fragment') return null;
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: `#ifdef SURFACEDETAIL
uniform sampler2D sdHullAlbedo;
uniform sampler2D sdHullNormal;
uniform sampler2D sdHangarAlbedo;
uniform sampler2D sdHangarNormal;
#endif`,
      // Avant l'éclairage : couleur et normale modifiées, reprises par les lumières et les reflets.
      CUSTOM_FRAGMENT_BEFORE_LIGHTS: `#ifdef SURFACEDETAIL
{
  vec3 sdP = (sdInvWorld * vec4(vPositionW, 1.0)).xyz;
  mat3 sdRot = mat3(sdInvWorld);
  vec3 sdN = normalize(sdRot * normalW);
  vec3 sdW = pow(abs(sdN), vec3(4.0));
  sdW /= max(sdW.x + sdW.y + sdW.z, 1e-4);
  bool sdIn = all(lessThan(abs(sdP), sdHangar));
  float sdTile = sdIn ? sdParams.y : sdParams.x;
  vec2 sdUvX = sdP.zy / sdTile;
  vec2 sdUvY = sdP.xz / sdTile;
  vec2 sdUvZ = sdP.xy / sdTile;
  vec3 sdCX, sdCY, sdCZ, sdNX, sdNY, sdNZ;
  if (sdIn) {
    sdCX = texture2D(sdHangarAlbedo, sdUvX).rgb; sdCY = texture2D(sdHangarAlbedo, sdUvY).rgb; sdCZ = texture2D(sdHangarAlbedo, sdUvZ).rgb;
    sdNX = texture2D(sdHangarNormal, sdUvX).xyz; sdNY = texture2D(sdHangarNormal, sdUvY).xyz; sdNZ = texture2D(sdHangarNormal, sdUvZ).xyz;
  } else {
    sdCX = texture2D(sdHullAlbedo, sdUvX).rgb; sdCY = texture2D(sdHullAlbedo, sdUvY).rgb; sdCZ = texture2D(sdHullAlbedo, sdUvZ).rgb;
    sdNX = texture2D(sdHullNormal, sdUvX).xyz; sdNY = texture2D(sdHullNormal, sdUvY).xyz; sdNZ = texture2D(sdHullNormal, sdUvZ).xyz;
  }
  vec3 sdColor = pow(sdCX * sdW.x + sdCY * sdW.y + sdCZ * sdW.z, vec3(2.2));
  surfaceAlbedo = mix(surfaceAlbedo, sdColor, sdParams.z);
  sdNX = sdNX * 2.0 - 1.0; sdNY = sdNY * 2.0 - 1.0; sdNZ = sdNZ * 2.0 - 1.0;
  // Relief par axe (mélange « UDN ») dans le repère du dock, puis retour au monde.
  vec3 sdPert = sdW.x * vec3(0.0, sdNX.y, sdNX.x * sign(sdN.x))
              + sdW.y * vec3(sdNY.x * sign(sdN.y), 0.0, sdNY.y)
              + sdW.z * vec3(sdNZ.x * sign(sdN.z), sdNZ.y, 0.0);
  normalW = normalize(normalW + transpose(sdRot) * sdPert * sdParams.w);
}
#endif`,
    };
  }
}
